import {
  Link,
  useLocation,
  useMatchRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import type { Entitlement } from "@magicweave/api";

import { orgTrail } from "./org-trail";

import {
  AppearanceSwitcher,
  Dropdown,
  NAV_ROW_ACTIVE,
  NAV_ROW_IDLE,
  WorkspaceFrame,
  WorkspaceHeader,
  Meter,
  RingMeter,
  WorkspaceRail,
  cn,
} from "@magicweave/design";

/** Everything inside the navigation drawer that a Tab can land on. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

import {
  useDraftDocuments,
  useEntitlement,
  usePlayerUsage,
  useEnvironments,
  useMembership,
  useProject,
  useProjects,
  useViewer,
} from "../api/hooks";
import { useAllowanceChip, useRunway } from "./allowance";
import {
  AI_TOOLS,
  COLLAPSE_NAV,
  EXPAND_NAV,
  creditsTitle,
  railLine,
  railPlayers,
} from "../copy/rail";
import { EnvironmentProvider } from "./environment";
import { useCurrentEnvironment, useEnvironmentSelection } from "./environment-context";
import { useCurrentOrg, useCurrentOrgSummary, useOrgs } from "./org";
import { useServing } from "./serving";
import { useSession } from "../auth/context";
import { Logo } from "./logo";
import { mcpTarget } from "./mcp-url";
import { MCP_NOT_AVAILABLE, MCP_ROW_LABEL, MCP_ROW_OFF_LABEL, NO_MCP } from "../copy/mcp-row";
import { BETA } from "../copy/beta";
import {
  EconomyIcon,
  EventsIcon,
  GamesIcon,
  HomeIcon,
  IntegrationsIcon,
  McpIcon,
  MoreIcon,
  NetworkIcon,
  ObserveIcon,
  PlanIcon,
  UsageIcon,
  PlayersIcon,
  SettingsIcon,
  SpendIcon,
  TeamIcon,
  type NavIcon,
} from "./nav-icons";

/**
 * The frame every screen sits in.
 *
 * Before this, navigation lived on two tab bars at two nesting levels: the
 * project's four destinations were visible from the project, and the five that
 * need an environment appeared only once you were inside one. So the first
 * screen after signing in showed four tabs, and most of the product — players,
 * live events, requests, the keys a developer needs — was somewhere you could
 * only reach if you already knew it existed.
 *
 * One sidebar, always showing everything, is what the architecture calls for
 * and what fixes that. The environment-scoped destinations resolve against a
 * chosen environment rather than a URL you happen to be under, so they are
 * reachable from anywhere, and the choice is a control in the header instead of
 * a fact about the route.
 *
 * **The frame has two shapes, not one shape with things hidden.** Above `lg`
 * it is a 52px strip, a 216px rail and the page. Below it the rail is a drawer,
 * the breadcrumb collapses to the one name that identifies where you are, the
 * environment gets a bar of its own directly under the strip, and five
 * destinations sit in a tab bar along the bottom. Which one is on screen is
 * decided once, in `useWideFrame`, rather than by hiding one copy of every
 * control with a `lg:` class — see that hook for why the duplicates had to go.
 */

/** Where a group of destinations sits in the shape of the work. */
interface Group {
  title: string;
  items: Array<{ to: string; label: string; scoped: boolean }>;
}

/**
 * Every destination the architecture names, including the ones nobody has built.
 *
 * This used to list only what existed, on the argument that a dead end costs
 * more trust than an absence. That was half right and it was the wrong half. An
 * absence is what someone hits when they go looking for the place to build a
 * shop, do not find one, and conclude the platform cannot do it — which is a
 * worse and much quieter failure than a link that opens a screen saying what
 * that screen will be. The cost of the link is one honest sentence, and the
 * screens behind these say plainly that they are not built and where to go
 * instead. See `NotBuiltYet` in the design system.
 *
 * The grouping is the architecture's, and it is by job rather than by module:
 * Operate is what you do while the game is running, Observe is how the running
 * game is doing, Build is what you change,
 * Ship is how a change reaches players, Account is what it costs. Most of the
 * people who live in Operate do not write code (decision 0012), which is why
 * nothing in this list is named after an internal system.
 */
const GROUPS: Group[] = [
  {
    title: "Operate",
    items: [
      { to: "/projects/$projectId", label: "Overview", scoped: false },
      { to: "/projects/$projectId/env/$envId/players", label: "Players", scoped: true },
      // Beside Players rather than beside Live events: both are about who is
      // in the game right now, where a shared room holds the board they are
      // playing on together — decisions/0034.
      { to: "/projects/$projectId/env/$envId/rooms", label: "Rooms", scoped: true },
      {
        to: "/projects/$projectId/env/$envId/economy-health",
        label: "Economy health",
        scoped: true,
      },
      { to: "/projects/$projectId/env/$envId/events", label: "Live events", scoped: true },
      { to: "/projects/$projectId/env/$envId/requests", label: "Requests", scoped: true },
    ],
  },
  {
    title: "Observe",
    items: [
      // Per environment: Magicweave's activity numbers and every analytics
      // connection belong to one environment, and "global" means every source
      // on one page, not every environment.
      { to: "/projects/$projectId/env/$envId/analytics", label: "Analytics", scoped: true },
    ],
  },
  {
    title: "Build",
    items: [
      // Economy is environment-scoped where the rest of Build is not, because
      // what it shows is the currencies an environment is actually serving
      // rather than what the draft says. Two environments of one project can
      // legitimately differ, and folding that away would be the lie.
      { to: "/projects/$projectId/env/$envId/economy", label: "Economy", scoped: true },
      // One flat row, like every other destination here. Events, Mechanics,
      // Rules and Variants used to live one level in — first on a second nav
      // that only appeared once you'd landed on one of them (`GameRulesNav`),
      // then as a disclosure in this row itself (`GameRulesGroup`, since
      // deleted). The switcher between them is inside the page now, a tab
      // strip with Events and Rules on it (`rules/game-rules-header.tsx`).
      // Mechanics and Variants are no longer reachable at all — their screens
      // are still here but nothing routes to them until they are refined
      // (`router.tsx`), so there is no URL to type either.
      { to: "/projects/$projectId/rules", label: "Game Rules", scoped: false },
      // Two entries, not one called Progression. A stat and a leaderboard are
      // things a game developer already has words for; "progression" is the
      // name of the module they happen to be implemented in, and naming a
      // screen after it is the failure decision 0012 exists to prevent. The
      // argument for the split, and for where game modes ended up, is in
      // router.tsx beside the routes.
      { to: "/projects/$projectId/stats", label: "Stats", scoped: false },
      { to: "/projects/$projectId/leaderboards", label: "Leaderboards", scoped: false },
      // Above Shops rather than inside them. This is where the thing a player
      // holds is written; a shop decides what it costs. An item that nothing
      // sells is still an item — a rule hands one over too — so filing it
      // under a shop would say otherwise.
      { to: "/projects/$projectId/inventory", label: "Inventory", scoped: false },
      // One entry where "Store & offers" and "Vouchers" used to be —
      // decisions/0035: a shop is a card, a studio makes as many as it needs,
      // and the platform's gift-card store is one more card on the same wall,
      // so the two destinations became one surface.
      { to: "/projects/$projectId/shops", label: "Shops", scoped: false },
    ],
  },
  {
    title: "Ship",
    // In the order a studio does them, which is not the order this was in.
    //
    // It read Releases, Environments, Connect & SDKs — the last step first —
    // while the note below claimed a studio "works down this group in order".
    // The first hour is: make an environment, take the credentials a build
    // authenticates with, turn on the sign-in those credentials are for, and
    // only then publish something. Releases is where that hour ends.
    //
    // This is the one group whose order is a sequence rather than a ranking,
    // so it is the one place ordering carries information rather than taste.
    items: [
      { to: "/projects/$projectId/environments", label: "Environments", scoped: false },
      {
        to: "/projects/$projectId/env/$envId/connect",
        label: "Connect & SDKs",
        scoped: true,
      },
      // Ship rather than Build or Account, because this is the last thing that
      // has to be true before a player can get past a game's first screen — it
      // sits with the keys a build authenticates with and the environments a
      // build points at.
      //
      // Environment-scoped for the reason the route is: a provider's client ids
      // are per environment, and a link that implied otherwise would be the
      // first step towards development credentials signing somebody in to the
      // live game.
      {
        to: "/projects/$projectId/env/$envId/sign-in",
        label: "Player sign-in",
        scoped: true,
      },
      // After Player sign-in: once a player is signed in to the game, this
      // signs them into the studio's own Firebase project. Per environment for
      // the same reason.
      {
        to: "/projects/$projectId/env/$envId/firebase-sign-in",
        label: "Firebase sign-in",
        scoped: true,
      },
      { to: "/projects/$projectId/releases", label: "Releases", scoped: false },
    ],
  },
  {
    title: "Account",
    items: [
      // First in Account now that the per-environment vendor-accounts screen
      // is gone (there is currently nothing else env-scoped in this group
      // besides Notifications below). Environment-scoped for the same reason
      // Economy is: a studio connects its own Firebase project and its own
      // Apple signing key per environment, so what is connected in
      // development is not what is connected in production.
      {
        to: "/projects/$projectId/env/$envId/notifications",
        label: "Notifications",
        scoped: true,
      },
      // Beside Notifications and not inside it. Project-scoped, unlike
      // Notifications: an ad network's app id is bought once per game, and
      // dev, staging and production share it — one connection reaches every
      // environment in this project, so the link needs no environment to
      // resolve. Its callback URL still differs per environment; the screen
      // itself shows each one, see `routes/ad-networks.tsx`.
      //
      // **Ad networks**, not "Ad Monetisation": this row said one thing and
      // the screen it opens said the other, which is decision 0012's own
      // failure in miniature. This is the name of a thing you can open; the
      // other was the name of a category.
      {
        to: "/projects/$projectId/ad-networks",
        label: "Ad networks",
        scoped: false,
      },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { projectId, orgId } = useParams({ strict: false });
  return (
    <EnvironmentProvider projectId={projectId}>
      <Frame projectId={projectId} orgId={orgId}>
        {children}
      </Frame>
    </EnvironmentProvider>
  );
}

/**
 * `lg`, asked once and answered in JavaScript rather than in fourteen classes.
 *
 * Tailwind's `lg:` prefix draws both frames and hides one, which is fine for a
 * gutter and wrong for this: the phone frame and the desktop frame name the
 * same things — the game, the environment, what it is serving — so hiding one
 * copy leaves two elements in the document reading "Production", one of them
 * invisible. Anything asking the page a question about the environment then has
 * to know which of the two it is looking at, a screen reader walking the strip
 * and a test locating by text included.
 *
 * So the frame picks a shape and renders only that one. `64rem` is `lg` and has
 * to stay in step with it; there is no way to read a Tailwind breakpoint at
 * runtime, so this is the one place the number is written down.
 *
 * The server snapshot is the wide frame. Nothing renders this on a server
 * today, and a first paint that assumes a desktop is the cheaper wrong guess —
 * a phone corrects it before the frame is interactive.
 */
const WIDE = "(min-width: 64rem)";

/**
 * How much deeper the environment shelf makes the chrome, written once because
 * two things resolve against it.
 *
 * The shelf only exists below `lg` and only inside a project — see
 * `EnvironmentBar` — and where it is on screen the chrome is two rows rather
 * than one. Anything sticking under the chrome has to clear both rows, so the
 * frame publishes the total as `--chrome-h` and the header is given that exact
 * height rather than left to measure whatever its contents come to. A chrome
 * whose real depth and the offset below it are two numbers that have to agree
 * is how the sticky group heading ended up hidden underneath it in the first
 * place — and they disagreed by a hairline even after this was written down,
 * until the header took the whole height and the shelf took what was left.
 */
const ENV_BAR_H = "2.75rem";

function subscribeToWidth(onChange: () => void) {
  const query = window.matchMedia(WIDE);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

// Loaded on the first click: the sheet is not on the path to anything else.
const ClaudeSheet = lazy(() =>
  import("./claude-sheet").then((m) => ({ default: m.ClaudeSheet })),
);

const preloadClaudeSheet = () => void import("./claude-sheet");

const McpSheet = lazy(() => import("./mcp-sheet").then((m) => ({ default: m.McpSheet })));

const preloadMcpSheet = () => void import("./mcp-sheet");

type Sheet = "claude" | "mcp";

function useWideFrame() {
  return useSyncExternalStore(
    subscribeToWidth,
    () => window.matchMedia(WIDE).matches,
    () => true,
  );
}

function Frame({
  projectId,
  orgId,
  children,
}: {
  projectId: string | undefined;
  orgId: string | undefined;
  /**
   * Give the whole width to the content: the rail collapses behind the strip's
   * own handle while the shared page measure keeps module edges aligned. The strip
   * itself stays — dropping it would strand somebody with no balance, no
   * account menu and no way out of the screen they are in.
   */
  children: ReactNode;
}) {
  const envId = useCurrentEnvironment();
  const pathname = useLocation({ select: (l) => l.pathname });
  const wide = useWideFrame();
  // Three rail states, not two: a project id gets the project sidebar, an org
  // id with no project gets the studio rail below it, and neither — Account,
  // /start — gets no rail at all. The two ids never both name a screen (the
  // project and studio scopes are siblings in the route tree, not nested), so
  // `projectId` is checked first only as a tie-break that can never fire.
  const workspace = Boolean(projectId) || Boolean(orgId);
  // The tab bar is the drawer's thumb-reachable front four, so it appears
  // exactly where the drawer does.
  const tabBar = workspace && !wide;
  // The environment gets a shelf of its own only where the strip could not
  // carry it, which is also the only case where the chrome is taller than one
  // strip. Decided here rather than inside `Header`, because the page — not
  // the header — is what has to be told how far down the chrome reaches.
  const envBar = !wide && Boolean(projectId);

  /**
   * The rail as an overlay, and why this exists at all.
   *
   * Below `lg` the rail is not drawn, so without this a phone would have a top
   * strip, a page and no way to reach Games, Team, Plan or Settings from
   * anywhere — the project sidebar's twenty destinations included.
   */
  const [railOpen, setRailOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      return localStorage.getItem("mw.console.rail.collapsed") === "true";
    } catch {
      return false;
    }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed((collapsed) => {
      try {
        localStorage.setItem("mw.console.rail.collapsed", String(!collapsed));
      } catch {
        /* The toggle still works when storage is unavailable. */
      }
      return !collapsed;
    });
  }, []);
  // Following a link inside the overlay must close it. Keying off the pathname
  // rather than each link's own click handler covers every way out of it,
  // including a redirect the destination itself performs.
  useEffect(() => {
    setRailOpen(false);
  }, [pathname]);
  // And so must growing the window past `lg`, where the drawer would otherwise
  // stand over a rail that is now a column of the page.
  useEffect(() => {
    if (wide) setRailOpen(false);
  }, [wide]);
  // The drawer is the only way to reach navigation on a phone, and at 200% zoom
  // on a laptop. It is a modal, so it has to behave like one: focus goes into
  // it, stays in it, and comes back to whatever opened it. Without that, a
  // keyboard user presses Enter on "Open navigation" and is left on a button
  // now underneath a scrim, tabbing through the whole page — `main` is rendered
  // before the drawer — before reaching the menu.
  //
  // The collapsed-rail flyout further down already does this. This is the same
  // contract for the drawer.
  const railOpener = useRef<HTMLElement | null>(null);
  const railPanel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!railOpen) return;
    railOpener.current = document.activeElement as HTMLElement | null;
    railPanel.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setRailOpen(false);
        return;
      }
      if (event.key !== "Tab" || !railPanel.current) return;
      const stops = [...railPanel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (stops.length === 0) return;
      const edge = event.shiftKey ? stops[0]! : stops[stops.length - 1]!;
      if (document.activeElement !== edge) return;
      event.preventDefault();
      (event.shiftKey ? stops[stops.length - 1]! : stops[0]!).focus();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      // Whatever closed it — Escape, the scrim, a route change, the window
      // growing past `lg` — focus returns to where it came from.
      railOpener.current?.focus();
    };
  }, [railOpen]);

  const openRail = useCallback(() => {
    setRailOpen(true);
  }, []);
  // Frame owns the sheets because the phone drawer unmounts when it closes. The
  // drawer's cleanup returns focus to its opener before the sheet records its
  // own, so Escape on a phone lands on "Open navigation", not on a dead row.
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const openSheet = useCallback((which: Sheet) => {
    setRailOpen(false);
    setSheet(which);
  }, []);
  const closeSheet = useCallback(() => {
    setSheet(null);
  }, []);
  const { target: mcp } = useMcp();

  const rail = projectId ? (
    <Sidebar
      projectId={projectId}
      envId={envId}
      collapsed={wide && railCollapsed}
      onToggleRail={toggleRail}
      sheet={sheet}
      onOpenSheet={openSheet}
    />
  ) : orgId ? (
    <StudioRail
      orgId={orgId}
      collapsed={wide && railCollapsed}
      onToggleRail={toggleRail}
      sheet={sheet}
      onOpenSheet={openSheet}
    />
  ) : null;

  return (
    <WorkspaceFrame
      // How far down the chrome reaches, for everything under it that sticks:
      // `RowGroup`'s heading and `Row`'s `scroll-mt` resolve against
      // `--chrome-h`. On a phone inside a project the environment shelf makes
      // the chrome two rows.
      style={
        envBar
          ? ({ "--chrome-h": `calc(var(--header-h) + ${ENV_BAR_H})` } as CSSProperties)
          : undefined
      }
      header={
        <Header
          projectId={projectId}
          envId={envId}
          wide={wide}
          envBar={envBar}
          railCollapsed={railCollapsed}
          // No rail to open means no handle: on Account and /start the button
          // would be a control that does nothing.
          onOpenRail={workspace ? openRail : undefined}
        />
      }
      rail={wide ? rail : null}
      // Room under the last row for the tab bar, which is fixed so that it
      // does not scroll away: its own 60px, plus the gutter below the page.
      mainClassName={tabBar ? "pb-19" : undefined}
      overlay={
        <>
          {tabBar ? (
            <TabBar projectId={projectId} orgId={orgId} envId={envId} onMore={openRail} />
          ) : null}
          {railOpen && rail ? (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              className="fixed inset-0 z-30 flex"
            >
              <button
                type="button"
                aria-label="Close navigation"
                onClick={() => {
                  setRailOpen(false);
                }}
                className="absolute inset-0 bg-ground/70 backdrop-blur-sm"
              />
              {/* No border of its own: the rail carries one, and two adjacent 1px
                  rules read as a 2px one that is a different grey. */}
              <div ref={railPanel} className="relative z-10 max-h-dvh overflow-y-auto">
                {rail}
              </div>
            </div>
          ) : null}
          {rail && sheet === "claude" ? (
            <Suspense fallback={null}>
              <ClaudeSheet open onClose={closeSheet} target={mcp} projectId={projectId} />
            </Suspense>
          ) : null}
          {rail && sheet === "mcp" ? (
            <Suspense fallback={null}>
              <McpSheet open onClose={closeSheet} target={mcp} />
            </Suspense>
          ) : null}
        </>
      }
    >
      {children}
    </WorkspaceFrame>
  );
}

/** The line a group draws under the rows an absent environment has dimmed. */
const NO_ENVIRONMENT = "These need an environment";

/**
 * The figures the rail puts beside three of its rows.
 *
 * Two are free: the project and its environments are already read by the top
 * strip, so these are the same two queries.
 * **The draft is not.** It is the only place a rule count exists — nothing
 * project-scoped reports one — and while most Build screens already hold it,
 * on Players or Requests this is a read those screens would not otherwise
 * make. Queries here default to a 30-second staleness and refetch on focus,
 * so that is a recurring cost for a number in a margin. It is here because
 * the alternative is a count that appears on some screens and not others,
 * which reads as broken rather than as thrifty.
 *
 * Keyed by route rather than by label so the wiring below cannot drift from
 * `GROUPS`: a row whose `to` is not in here simply has no figure.
 */
function useRailCounts(
  projectId: string,
  envId: string | undefined,
): Partial<Record<string, string>> {
  const project = useProject(projectId);
  const environments = useEnvironments(projectId);
  const draft = useDraftDocuments(projectId);

  const env = (environments.data ?? []).find((e) => e.id === envId);
  const rules = (draft.data ?? []).filter((doc) => doc.path.startsWith("rules/rules/")).length;

  return {
    "/projects/$projectId/env/$envId/players":
      env === undefined ? undefined : env.players.toLocaleString(),
    "/projects/$projectId/loops": rules === 0 ? undefined : String(rules),
    "/projects/$projectId/environments":
      project.data === undefined ? undefined : String(project.data.environments),
  };
}

const GROUP_ICONS: Record<string, NavIcon> = {
  Operate: EventsIcon,
  Observe: ObserveIcon,
  Build: GamesIcon,
  Ship: IntegrationsIcon,
  Account: SettingsIcon,
  "Shared Loyalty": NetworkIcon,
};

function RailGroup({
  title,
  open,
  active,
  collapsed,
  flyoutOpen,
  onToggle,
  onFlyout,
  children,
}: {
  title: string;
  open: boolean;
  active?: boolean;
  collapsed: boolean;
  flyoutOpen: boolean;
  onToggle: () => void;
  onFlyout: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  const Icon = GROUP_ICONS[title] ?? GamesIcon;
  const id = `rail-${title.toLowerCase()}`;
  const hidden = !open || collapsed;
  return (
    <section>
      <button
        type="button"
        aria-label={title}
        title={collapsed ? title : undefined}
        aria-expanded={collapsed ? flyoutOpen : open}
        aria-controls={collapsed ? (flyoutOpen ? "rail-flyout" : undefined) : id}
        data-rail-group
        onClick={collapsed ? onFlyout : onToggle}
        className={cn(
          "rail-section flex w-full items-center gap-2.5 overflow-hidden rounded-sm px-3 py-2 text-ui font-medium hover:bg-surface-2/35",
          "focus-visible:outline-2 focus-visible:outline-accent",
          (open && !collapsed) || (active && collapsed)
            ? "text-ink"
            : "text-ink-2 hover:text-ink",
        )}
      >
        <Icon
          className={cn("size-4 shrink-0", active && (open || collapsed) && "text-accent-text")}
        />
        <span className="rail-fade">{title}</span>
        <span aria-hidden="true" className="rail-fade ml-auto">
          <span className={cn("block transition-transform", !open && "-rotate-90")}>
            <CaretIcon />
          </span>
        </span>
      </button>
      <div
        id={id}
        aria-hidden={hidden}
        inert={hidden}
        className={cn(
          "ml-5 grid rail-group-content",
          hidden ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
        )}
      >
        <div
          className={cn("min-h-0 overflow-hidden border-l border-rule pl-2", !hidden && "pb-2")}
        >
          {children}
        </div>
      </div>
    </section>
  );
}

function Sidebar({
  projectId,
  envId,
  collapsed,
  onToggleRail,
  sheet,
  onOpenSheet,
}: {
  projectId: string;
  envId: string | undefined;
  collapsed: boolean;
  onToggleRail: () => void;
  sheet: Sheet | null;
  onOpenSheet: (which: Sheet) => void;
}) {
  const orgId = useCurrentOrg();
  const counts = useRailCounts(projectId, envId);
  const pathname = useLocation({ select: (l) => l.pathname });
  const activeGroup =
    GROUPS.find((group) =>
      group.items.some((item) => {
        const path = item.to.replace("$projectId", projectId).replace("$envId", envId ?? "");
        return (
          pathname === path ||
          (item.to !== "/projects/$projectId" && pathname.startsWith(`${path}/`))
        );
      }),
    )?.title ?? "Operate";
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroup);
  const [flyout, setFlyout] = useState<{ title: string; top: number; left: number } | null>(
    null,
  );
  useEffect(() => {
    setOpenGroup(activeGroup);
    setFlyout(null);
  }, [activeGroup, pathname]);
  useEffect(() => {
    if (!collapsed) setFlyout(null);
  }, [collapsed]);
  useEffect(() => {
    if (!flyout) return;
    if (document.activeElement?.matches("[data-rail-group]")) {
      document.querySelector<HTMLElement>("[data-rail-flyout] a")?.focus();
    }
    const dismiss = (event: PointerEvent) => {
      if (
        event.target instanceof Element &&
        (event.target.closest("[data-rail-flyout]") ||
          event.target.closest("[data-rail-group]"))
      )
        return;
      setFlyout(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const group = [
          ...document.querySelectorAll<HTMLButtonElement>("[data-rail-group]"),
        ].find((button) => button.getAttribute("aria-label") === flyout.title);
        setFlyout(null);
        group?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    window.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      window.removeEventListener("keydown", escape);
    };
  }, [flyout]);
  const toggleFlyout = (title: string, event: ReactMouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const rail = event.currentTarget
      .closest("[aria-label='Sections']")
      ?.getBoundingClientRect();
    setFlyout((current) =>
      current?.title === title
        ? null
        : {
            title,
            left: (rail?.right ?? rect.right) + 12,
            top: Math.max(8, Math.min(rect.top - 8, window.innerHeight - 350)),
          },
    );
  };

  return (
    <>
      <WorkspaceRail aria-label="Sections" collapsed={collapsed}>
        <RailTop collapsed={collapsed} />
        <div
          className="min-h-0 flex-1 [scrollbar-width:thin] overflow-x-hidden overflow-y-auto"
          data-nav-links
        >
          <div className="mt-3 space-y-1">
            {GROUPS.map((group) => (
              <RailGroup
                key={group.title}
                title={group.title}
                open={openGroup === group.title}
                active={activeGroup === group.title}
                collapsed={collapsed}
                flyoutOpen={flyout?.title === group.title}
                onToggle={() => setOpenGroup(openGroup === group.title ? null : group.title)}
                onFlyout={(event) => toggleFlyout(group.title, event)}
              >
                {group.items.map((item) => (
                  <SideLink
                    key={item.to}
                    to={item.to}
                    params={
                      item.scoped ? (envId ? { projectId, envId } : undefined) : { projectId }
                    }
                    tag={item.scoped && envId === undefined ? "—" : counts[item.to]}
                    tagTone={
                      item.to === "/projects/$projectId/environments" && counts[item.to] === "0"
                        ? "warning"
                        : undefined
                    }
                  >
                    {item.label}
                  </SideLink>
                ))}
                {envId === undefined && group.items.some((item) => item.scoped) ? (
                  <p className="px-3 pt-1 text-micro text-ink-3">{NO_ENVIRONMENT}</p>
                ) : null}
              </RailGroup>
            ))}
            {orgId ? (
              <RailGroup
                title="Shared Loyalty"
                open={openGroup === "Shared Loyalty"}
                collapsed={collapsed}
                flyoutOpen={flyout?.title === "Shared Loyalty"}
                onToggle={() =>
                  setOpenGroup(openGroup === "Shared Loyalty" ? null : "Shared Loyalty")
                }
                onFlyout={(event) => toggleFlyout("Shared Loyalty", event)}
              >
                <NetworkGroup orgId={orgId} projectId={projectId} envId={envId} />
              </RailGroup>
            ) : null}
          </div>
        </div>
        <SidebarFooter
          collapsed={collapsed}
          onToggleRail={onToggleRail}
          sheet={sheet}
          onOpenSheet={onOpenSheet}
        />
      </WorkspaceRail>
      {collapsed && flyout
        ? createPortal(
            <div
              id="rail-flyout"
              data-rail-flyout
              className="fixed z-50 max-h-[min(70dvh,28rem)] w-55 glass-enter overflow-y-auto rounded-[var(--radius-panel)] border border-rule bg-surface p-2"
              style={{ top: flyout.top, left: flyout.left }}
            >
              <p className="px-3 py-2 text-meta font-semibold text-ink">{flyout.title}</p>
              {flyout.title === "Shared Loyalty" && orgId ? (
                <NetworkGroup orgId={orgId} projectId={projectId} envId={envId} />
              ) : (
                GROUPS.find((group) => group.title === flyout.title)?.items.map((item) => (
                  <SideLink
                    key={item.to}
                    to={item.to}
                    params={
                      item.scoped ? (envId ? { projectId, envId } : undefined) : { projectId }
                    }
                    tag={item.scoped && envId === undefined ? "—" : counts[item.to]}
                  >
                    {item.label}
                  </SideLink>
                ))
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function RailTop({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="relative mb-3 min-h-9 shrink-0">
      <StudioName inRail collapsed={collapsed} />
    </div>
  );
}

function RailHandle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? EXPAND_NAV : COLLAPSE_NAV;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={label}
      aria-expanded={!collapsed}
      className={cn(
        "rail-handle absolute z-10 hidden size-6 place-items-center rounded-full border lg:grid",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        collapsed
          ? "border-accent bg-accent text-accent-ink hover:brightness-105"
          : "border-rule-strong bg-surface text-ink-2 hover:text-ink",
      )}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" className="rail-turn size-3" fill="none">
        <path
          d="m14.5 6-6 6 6 6"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/** This deployment's own MCP server, from the viewer: nothing about it is hard-coded here. */
function useMcp() {
  const viewer = useViewer();
  return { target: mcpTarget(viewer.data?.mcp_url), known: Boolean(viewer.data) };
}

const ROW =
  "flex h-8 w-full overflow-hidden items-center gap-2 rounded-sm px-2.75 in-data-[rail=closed]:px-2 text-left text-ui text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-accent";
const OPEN = "bg-surface-2 text-ink";

/** Opens the MCP sheet; off, with its reason, when there is no MCP server. */
function McpRow({
  collapsed,
  open,
  onOpen,
}: {
  collapsed: boolean;
  open: boolean;
  onOpen: () => void;
}) {
  const { target } = useMcp();
  const icon = <McpIcon className="size-[18px] shrink-0" />;
  if (target.kind === "none") {
    return (
      <button
        type="button"
        disabled
        aria-label={MCP_ROW_OFF_LABEL}
        title={NO_MCP}
        className={cn(ROW, "disabled:pointer-events-none disabled:opacity-50")}
      >
        {icon}
        <span className="rail-fade">{MCP_ROW_LABEL}</span>
        <span className="rail-fade ml-auto font-mono text-micro font-medium tracking-wide text-ink-2 uppercase">
          {MCP_NOT_AVAILABLE}
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={MCP_ROW_LABEL}
      title={collapsed ? MCP_ROW_LABEL : undefined}
      onClick={onOpen}
      onPointerEnter={preloadMcpSheet}
      onFocus={preloadMcpSheet}
      className={cn(ROW, open && OPEN)}
    >
      {icon}
      <span className="rail-fade">{MCP_ROW_LABEL}</span>
    </button>
  );
}

/**
 * Opens the Build with Claude sheet; off, with its reason, when there is no MCP
 * server. It never navigates: the sheet says what to add for this deployment.
 */
function BuildWithClaudeRow({
  open,
  onOpenClaude,
}: {
  open: boolean;
  onOpenClaude: () => void;
}) {
  const { target, known } = useMcp();
  const mark = (
    <img src="/brand/claude.png" alt="" aria-hidden="true" className="size-[18px] shrink-0" />
  );
  const label = <span className="rail-fade">Build with Claude</span>;

  if (target.kind === "none") {
    return (
      <>
        <button
          type="button"
          disabled
          aria-label="Build with Claude, not available"
          title={NO_MCP}
          className={cn(
            "rail-claude claude-nav-link",
            ROW,
            "bg-sunk/50 disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          {mark}
          {label}
        </button>
        {known ? (
          <div className="rail-fold">
            <div>
              <p className="px-2 pt-1 text-micro text-ink-3">{NO_MCP}</p>
            </div>
          </div>
        ) : null}
        {known ? (
          <div className="rail-unfold" aria-hidden="true">
            <div>
              <p className="text-center text-micro text-ink-3">No MCP</p>
            </div>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label="Build with Claude"
      title="Build with Claude"
      onClick={onOpenClaude}
      onPointerEnter={preloadClaudeSheet}
      onFocus={preloadClaudeSheet}
      className={cn("rail-claude claude-nav-link", ROW, "bg-sunk/50", open && OPEN)}
    >
      {mark}
      {label}
    </button>
  );
}

function SidebarFooter({
  collapsed = false,
  onToggleRail,
  sheet,
  onOpenSheet,
}: {
  collapsed?: boolean;
  onToggleRail: () => void;
  sheet: Sheet | null;
  onOpenSheet: (which: Sheet) => void;
}) {
  const { chip } = useAllowanceChip();
  const id = useId();
  return (
    <div className="mt-2 flex shrink-0 flex-col gap-2 pt-2">
      <div className="relative">
        <RailHandle collapsed={collapsed} onToggle={onToggleRail} />
        <div role="group" aria-labelledby={id} className="flex flex-col">
          <div className="rail-fold">
            <div>
              <p
                id={id}
                className="px-2 pb-1 font-mono text-micro font-medium tracking-wide whitespace-nowrap text-ink-3 uppercase"
              >
                {AI_TOOLS}
              </p>
            </div>
          </div>
          <McpRow
            collapsed={collapsed}
            open={sheet === "mcp"}
            onOpen={() => onOpenSheet("mcp")}
          />
          <BuildWithClaudeRow
            open={sheet === "claude"}
            onOpenClaude={() => onOpenSheet("claude")}
          />
        </div>
      </div>
      <RunwaySlot collapsed={collapsed} />
      {chip ? <UsageChips collapsed={collapsed} /> : null}
      <div className="border-t border-rule" />
      <AccountMenu expanded />
    </div>
  );
}

/**
 * The runway notice, loaded on demand: it draws only from day 60 of a free
 * period, so its words and markup stay off the initial payload every other
 * studio downloads. The gate is the server's `runway` state, read eagerly.
 */
const RunwayBanner = lazy(() => import("./runway-banner"));

function RunwaySlot({ collapsed }: { collapsed: boolean }) {
  const { orgId, runway, settled } = useRunway();
  if (!orgId || !runway || !settled) return null;
  return (
    <Suspense fallback={null}>
      <RunwayBanner orgId={orgId} runway={runway} collapsed={collapsed} />
    </Suspense>
  );
}

/**
 * The studio rail — decisions/0038's third frame state, for an organisation
 * screen with no project in view.
 *
 * Before this, Team, Plan, Network and Settings rendered with no navigation
 * around them at all (`Frame` had exactly two states); this is what makes them a scope rather than four bare pages whose
 * only exit is the wordmark. Games is the studio's own index route, exact-
 * matched the same way Overview is in `SideLink` below, so it does not stay lit
 * while looking at Team or Plan.
 *
 * Reuses `SideLink` rather than a second link component — this rail and the
 * project sidebar are two renderings of the same idea, and a second copy would
 * be bundle weight paying for nothing the first didn't already do.
 *
 * It was exported and had a second home: the AI Studio's workspace mounted it
 * as an icon column, narrowed by a `collapsed` prop, because that screen
 * dropped the rail to give the width to the work and would otherwise have had
 * no way out but the wordmark. That screen is gone, so this is one component
 * with one home again and neither the export nor the prop is reachable.
 *
 * Settings follows the other studio destinations so it remains discoverable
 * without searching the foot of the rail.
 */
function StudioRail({
  orgId,
  collapsed,
  onToggleRail,
  sheet,
  onOpenSheet,
}: {
  orgId: string;
  collapsed: boolean;
  onToggleRail: () => void;
  sheet: Sheet | null;
  onOpenSheet: (which: Sheet) => void;
}) {
  const row = (to: string, label: string, icon: NavIcon, tag?: string) => (
    <SideLink key={to} to={to} params={{ orgId }} icon={icon} collapsed={collapsed} tag={tag}>
      {label}
    </SideLink>
  );

  return (
    <WorkspaceRail aria-label="Studio sections" collapsed={collapsed}>
      <RailTop collapsed={collapsed} />
      <div
        className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
        data-nav-links
      >
        <div className="flex flex-col gap-px">
          {row("/orgs/$orgId", "Games", GamesIcon)}
          {row("/orgs/$orgId/team", "Team", TeamIcon)}
          {row("/orgs/$orgId/plan", "Plan", PlanIcon)}
          {row("/orgs/$orgId/usage", "Usage", UsageIcon)}
          {row("/orgs/$orgId/network", "Shared Loyalty", NetworkIcon, BETA)}
          {/* An org-owned account fact, not a game fact — fal.ai, Ideogram
              and Stability AI are one connection shared by every project and
              environment in the studio, so they live beside Network and
              Settings rather than under any one game. */}
          {row("/orgs/$orgId/integrations", "Integrations", IntegrationsIcon)}
          {row("/orgs/$orgId/settings", "Settings", SettingsIcon)}
        </div>
      </div>
      <SidebarFooter
        collapsed={collapsed}
        onToggleRail={onToggleRail}
        sheet={sheet}
        onOpenSheet={onOpenSheet}
      />
    </WorkspaceRail>
  );
}

/**
 * Network, last and on its own — not a fifth row folded into Account.
 *
 * `GROUPS` above is by job and is entirely project-scoped; a membership is
 * the organisation's, the same reason `networkRoute` in router.tsx gives for
 * living outside `/projects/$projectId` at all. Rather than generalise every
 * `SideLink` call site in `GROUPS` to carry org-scoped params for the sake of
 * one group, this reuses `SideLink` directly with `{ orgId }` — the component
 * itself is already generic over `params`, and only the loop above assumed
 * project scope. It still takes `projectId` and `envId`, though, for
 * Authentication below — the one sub-item that is not org-scoped.
 *
 * **Always one entry, on decision `0012`'s own argument already made above**:
 * an absence costs more trust than a dead end, and before this the only way in
 * was three clicks behind the avatar menu on Account, or the card on Studio
 * settings. Two more sub-items appear once `useMembership` reports `approved`
 * — Quota is not one of them: it already lives on this same Network page
 * rather than a page of its own, and a sub-item pointing at the same URL as
 * its own parent would be a link to nowhere new. The shelf used to be a third,
 * and moved: it is the Magicweave Shop card on the project's Shops wall now
 * (`decisions/0035`), not an org-level destination of its own.
 *
 * **Authentication is a second door to a screen `GROUPS`' Ship section
 * already opens as "Player sign-in", and that duplication is deliberate
 * rather than the case the Quota reasoning above rules out.** A studio's
 * coins mean nothing until a player can link a verified identity to a
 * network account, and a guest cannot — the identity module refuses a guest
 * outright, having no credential to prove an address with. Someone reading
 * "you're in the network, now what" has no reason yet to already know that
 * fact lives under Ship, so it is named here too, scoped to the project and
 * environment the sidebar already has in view.
 *
 * Called plain, with no `keepPrevious` — this is gating logic, not chrome,
 * and must not still say "approved" for a heartbeat after switching to an
 * organisation that has not joined.
 *
 * **Not a replacement for Account's own ghost button.** That list can show
 * every organisation a person belongs to; this can only ever link to the one
 * currently in view. A person in two studios still needs Account's link to
 * reach the *other* one's Network page, so both stay.
 */
function NetworkGroup({
  orgId,
  projectId,
  envId,
}: {
  orgId: string;
  projectId: string;
  envId: string | undefined;
}) {
  const membership = useMembership(orgId);
  const approved = membership.data?.state === "approved";
  return (
    <div className="flex flex-col gap-px">
      <SideLink to="/orgs/$orgId/network" params={{ orgId }} tag={BETA}>
        Shared Loyalty
      </SideLink>
      {approved ? (
        <>
          <SideLink
            to="/projects/$projectId/env/$envId/sign-in"
            params={envId ? { projectId, envId } : undefined}
          >
            Authentication
          </SideLink>
          <SideLink to="/orgs/$orgId/network/acceptance" params={{ orgId }}>
            Coin activity
          </SideLink>
        </>
      ) : null}
    </div>
  );
}

/**
 * Production's mark, and the one thing in the frame that is allowed to be red.
 *
 * `critical` **and** the word: this is never on screen without the environment
 * named beside it, because a dot doing that job alone would be severity carried
 * by colour, which is the one thing the house rules refuse outright.
 */
function EnvironmentDot({ production }: { production: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        production ? "bg-critical" : "bg-positive",
      )}
    />
  );
}

function SideLink({
  to,
  params,
  children,
  icon: Icon,
  tag,
  tagTone,
  collapsed = false,
}: {
  to: string;
  params?: object | undefined;
  children: ReactNode;
  /**
   * The studio rail's rows carry one. The project rail's do not: it has
   * five group headings doing that job, and twenty-four marks nobody drew
   * would be twenty-four guesses.
   *
   * There used to be an `iconAccent` beside this, for the one row that was a
   * door onto a different kind of work, and a `collapsed` for the narrow icon
   * column that row's screen rendered. Both were the AI Studio's and both went
   * with it. Neither would have been reported by the compiler or the linter —
   * an optional prop nobody passes is not an error anywhere.
   */
  icon?: NavIcon;
  collapsed?: boolean;
  /**
   * A figure in the margin — how many players, how many rules.
   *
   * **`aria-hidden`, which is the opposite of what `Chip.count` does**, and
   * the difference is worth stating because the two look identical. A
   * filter chip's count tells you what the button will *do*: "Working drafts
   * 8" means pressing it leaves eight rows, so the number belongs to the
   * name. A nav count tells you nothing about where the link goes, and the
   * screen it opens states the same figure properly. Announcing "Players
   * 128" would make every row of the rail read as a two-part label for no
   * gain.
   *
   * It also keeps every name in the rail exactly the word it was, which is
   * what `navigation.spec.ts` matches on for twenty-two rows.
   */
  tag?: string;
  /**
   * A figure that is the problem rather than the measurement — no
   * environments, on the row that makes one. Never alone: the group it sits
   * in carries `NO_ENVIRONMENT` in words.
   */
  tagTone?: "warning";
}) {
  const matchRoute = useMatchRoute();
  // Overview and Games are each their own scope's index route, so a fuzzy
  // match would light them on every screen in the scope. Everything else
  // keeps fuzzy, because a child screen should leave its section selected.
  const exact = to === "/projects/$projectId" || to === "/orgs/$orgId";
  const active = Boolean(params && matchRoute({ to, params, fuzzy: !exact } as never));

  const label = <span className={cn("min-w-0 truncate", Icon && "rail-fade")}>{children}</span>;
  const figure =
    tag === undefined ? null : (
      <span
        aria-hidden="true"
        // Dim and mono, the same pairing `Chip.count` settled on and for the
        // reason recorded there: the accent is a fill, 2.25:1 on a raised
        // surface, and the selected row already says which one it is without a
        // colour helping.
        className={cn(
          "ml-auto pl-2 font-mono text-micro tabular-nums",
          Icon && "rail-fade",
          tagTone === "warning" ? "text-warning-text" : "text-ink-3",
        )}
      >
        {tag}
      </span>
    );

  if (!params) {
    return (
      <span
        aria-disabled="true"
        title={NO_ENVIRONMENT}
        className="flex items-center gap-2.5 rounded-sm px-3 py-1.75 text-ui text-ink-3"
      >
        {Icon ? <Icon className="size-4 shrink-0" /> : null}
        {label}
        {figure}
      </span>
    );
  }
  return (
    <Link
      to={to}
      title={collapsed && typeof children === "string" ? children : undefined}
      aria-label={collapsed && typeof children === "string" ? children : undefined}
      params={params as never}
      // The router has its own idea of active, and it was disagreeing with
      // ours. `Link` marks itself `aria-current="page"` and adds an `active`
      // class whenever the route matches, and its default match is fuzzy — so
      // on every organisation screen the router told a screen reader that
      // **Games** was the current page while the highlight sat on AI Studio.
      // The `exact` above is the same rule this component was already applying
      // to the styling; handing it to the router as well is what makes the two
      // agree.
      activeOptions={{ exact }}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group flex items-center gap-2.5 rounded-sm text-ui",
        // 8px against 7px: a row carrying a mark is a taller row, and the
        // design gives the studio rail the extra pixel rather than letting the
        // icons crowd their own baseline.
        Icon ? "rail-link py-2" : "py-1.5 nav-motion",
        "px-3",
        active ? NAV_ROW_ACTIVE : NAV_ROW_IDLE,
      )}
    >
      {Icon ? (
        <Icon
          className={cn(
            "size-4 shrink-0 transition-colors",
            active ? "text-accent-text" : "text-ink-3 group-hover:text-ink-2",
          )}
        />
      ) : null}
      {label}
      {figure}
    </Link>
  );
}

/**
 * The top strip: which studio, which game, which environment, and what that
 * environment is serving.
 *
 * The last of those is the one that used to be missing, and it is the one that
 * makes the others actionable. "Development" tells you where you are;
 * "Development · working draft" tells you that the thing you just saved is
 * already live there, and "Production · Release #12" tells you it is not.
 *
 * The strip is 52px in both frames. What changes below `lg` is what it can
 * afford to carry: the breadcrumb becomes the one name that identifies where
 * you are, and the environment — the fact this row exists to keep honest —
 * moves out into a bar of its own directly underneath, where it gets a whole
 * line rather than the 57px the breadcrumb could spare it.
 */
function Header({
  projectId,
  envId,
  wide,
  envBar,
  onOpenRail,
  railCollapsed,
}: {
  projectId: string | undefined;
  envId: string | undefined;
  wide: boolean;
  envBar: boolean;
  onOpenRail?: () => void;
  railCollapsed: boolean;
}) {
  return (
    <WorkspaceHeader
      railOffset={wide && onOpenRail ? (railCollapsed ? "collapsed" : "expanded") : "none"}
      className={envBar ? undefined : "h-[var(--header-h)]"}
      style={envBar ? { height: `calc(var(--header-h) + ${ENV_BAR_H})` } : undefined}
    >
      <div
        className={cn(
          "flex items-center gap-3 px-3.5 lg:px-2",
          envBar ? "h-[var(--header-h)] shrink-0" : "h-full",
        )}
      >
        {onOpenRail && !wide ? (
          <button
            type="button"
            aria-label="Open navigation"
            onClick={onOpenRail}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-ink hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
          >
            <RailIcon className="size-[19px]" />
          </button>
        ) : null}
        {!onOpenRail ? (
          <Link
            to="/"
            aria-label="Magicweave"
            className="flex shrink-0 items-center gap-2.5 text-ui font-semibold text-ink"
          >
            <Logo className="size-5 shrink-0" />
            {wide ? <span>Magicweave</span> : null}
          </Link>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {projectId ? (
            <ProjectSwitcher projectId={projectId} />
          ) : !wide ? (
            <CompactTitle projectId={projectId} />
          ) : (
            <OrgCrumb />
          )}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-4">
          {wide && projectId ? (
            <EnvironmentSwitcher projectId={projectId} envId={envId} />
          ) : null}
          <AppearanceSwitcher />
          {!onOpenRail ? <AccountMenu /> : null}
        </div>
      </div>
      {envBar && projectId ? <EnvironmentBar projectId={projectId} envId={envId} /> : null}
    </WorkspaceHeader>
  );
}

function AccountMenu({ expanded = false }: { expanded?: boolean }) {
  const viewer = useViewer();
  const navigate = useNavigate();
  const { signOut } = useSession();
  return (
    <Dropdown
      className={expanded ? "w-full [&>button]:w-full" : "flex justify-center"}
      placement={expanded ? "above" : "below"}
      align={expanded ? "start" : "end"}
      label={`Account: ${viewer.data?.email ?? "signed in"}`}
      trigger={
        <span
          className={cn(
            "flex items-center gap-2.5 rounded-sm text-ink-2 hover:bg-surface-2",
            expanded && "rail-account w-full overflow-hidden px-2 text-left",
          )}
        >
          <span className="inline-grid size-8 shrink-0 place-items-center rounded-sm border border-rule-strong bg-surface-2 text-ui font-medium text-ink">
            {viewer.data?.email ? (
              viewer.data.email.slice(0, 1).toUpperCase()
            ) : (
              <UserIcon className="size-3.5" />
            )}
          </span>
          {expanded ? (
            <>
              <span className="rail-fade min-w-0 flex-1">
                <span className="block text-ui font-medium text-ink">My account</span>
                <span className="block truncate text-meta text-ink-3">
                  {viewer.data?.email ?? "Signed in"}
                </span>
              </span>
              <span className="rail-fade grid place-items-center">
                <CaretIcon />
              </span>
            </>
          ) : null}
        </span>
      }
      items={[
        {
          id: "account",
          label: "Account settings",
          onSelect: () => void navigate({ to: "/account" }),
        },
        { id: "sign-out", label: "Sign out", tone: "danger", onSelect: () => void signOut() },
      ]}
    />
  );
}

/**
 * The breadcrumb, where there is room for only the last level of it.
 *
 * The rail is behind a handle at this size and most screens do not name the
 * game they are about, so this is the one place on a phone that says which
 * game — or, outside a project, which studio — is in view. The levels above it
 * are one press away in the drawer, which is where a reader the answer
 * surprises is going anyway.
 */
function CompactTitle({ projectId }: { projectId: string | undefined }) {
  const project = useProject(projectId ?? "");
  const org = useCurrentOrgSummary();
  const name = projectId ? project.data?.name : org?.name;
  if (name === undefined) return null;
  return <span className="min-w-0 truncate text-ui font-semibold text-ink">{name}</span>;
}

/**
 * The environment on a line of its own, under the strip — the phone's half of
 * the switcher the wide strip carries inline.
 *
 * It is chrome rather than page: every scoped row in the drawer resolves
 * against it, so it belongs to the frame the way the breadcrumb does, and a
 * reader who cannot see the rail has nothing else on screen saying whether they
 * are about to change production. `sunk` under a hairline, so it reads as a
 * second shelf of the strip rather than as the first row of the page.
 *
 * Only inside a project, and only ever one of the two: the strip renders the
 * switcher when the frame is wide and this when it is not.
 */
function EnvironmentBar({
  projectId,
  envId,
}: {
  projectId: string;
  envId: string | undefined;
}) {
  return (
    // Whatever the header has left after the strip, rather than a height of its
    // own: the header is the element with the stated depth, because that depth
    // is what everything below the chrome sticks to. See `ENV_BAR_H`.
    <div className="flex min-h-0 grow items-center gap-2.5 border-t border-rule bg-material px-3.5">
      <span className="hidden shrink-0 text-meta text-ink-3 sm:block">Environment</span>
      <EnvironmentSwitcher projectId={projectId} envId={envId} />
    </div>
  );
}

/**
 * The four destinations a phone reader actually opens, and the drawer behind
 * the fifth.
 *
 * Not a second navigation. Every one of these is a row of the rail the handle
 * opens, in the same words, and **More** opens that rail rather than a menu of
 * its own — what the bar buys is the thumb, four presses that would otherwise
 * each be two on the screens people keep open.
 *
 * The four differ by scope because the scopes do: a studio has no environment
 * and no game in view, so its four are the studio's own. A scoped destination
 * with no environment yet is drawn and not linked, exactly as the rail draws
 * it — a tab that 404s is worse than a tab that is plainly not ready.
 */
function TabBar({
  projectId,
  orgId,
  envId,
  onMore,
}: {
  projectId: string | undefined;
  orgId: string | undefined;
  envId: string | undefined;
  onMore: () => void;
}) {
  const matchRoute = useMatchRoute();

  const scoped = envId ? { projectId, envId } : undefined;
  const tabs: Array<{
    to: string;
    label: string;
    icon: NavIcon;
    params?: object;
    exact?: boolean;
  }> = projectId
    ? [
        {
          to: "/projects/$projectId",
          label: "Overview",
          icon: HomeIcon,
          params: { projectId },
          exact: true,
        },
        {
          to: "/projects/$projectId/env/$envId/events",
          label: "Events",
          icon: EventsIcon,
          params: scoped,
        },
        {
          to: "/projects/$projectId/env/$envId/players",
          label: "Players",
          icon: PlayersIcon,
          params: scoped,
        },
        {
          to: "/projects/$projectId/env/$envId/economy",
          label: "Economy",
          icon: EconomyIcon,
          params: scoped,
        },
      ]
    : orgId
      ? [
          {
            to: "/orgs/$orgId",
            label: "Games",
            icon: GamesIcon,
            params: { orgId },
            exact: true,
          },
          { to: "/orgs/$orgId/team", label: "Team", icon: TeamIcon, params: { orgId } },
        ]
      : [];

  const on = tabs.map((tab) =>
    Boolean(
      tab.params && matchRoute({ to: tab.to, params: tab.params, fuzzy: !tab.exact } as never),
    ),
  );

  const cell = "flex flex-1 flex-col items-center justify-center gap-0.5 text-micro";

  return (
    <nav
      aria-label="Destinations"
      className="fixed inset-x-0 bottom-0 z-20 flex h-15 items-stretch border-t border-rule bg-sunk"
    >
      {tabs.map((tab, i) =>
        tab.params ? (
          <Link
            key={tab.to}
            to={tab.to}
            params={tab.params as never}
            activeOptions={{ exact: Boolean(tab.exact) }}
            aria-current={on[i] ? "page" : undefined}
            className={cn(cell, on[i] ? "text-accent-text" : "text-ink-2")}
          >
            <tab.icon className="size-[19px]" />
            {tab.label}
          </Link>
        ) : (
          <span
            key={tab.to}
            aria-disabled="true"
            title={NO_ENVIRONMENT}
            className={cn(cell, "text-ink-3")}
          >
            <tab.icon className="size-[19px]" />
            {tab.label}
          </span>
        ),
      )}
      {/* A door onto the rail rather than a destination, so it lights when none
          of the four does: on every other screen in the scope, the rail is
          where the reader is. */}
      <button
        type="button"
        onClick={onMore}
        className={cn(cell, on.some(Boolean) ? "text-ink-2" : "text-accent-text")}
      >
        <MoreIcon className="size-[19px]" />
        More
      </button>
    </nav>
  );
}

function RailIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5 20v-1.4A3.6 3.6 0 0 1 8.6 15h6.8a3.6 3.6 0 0 1 3.6 3.6V20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TONE_TEXT = {
  neutral: "text-accent-text",
  warning: "text-warning-text",
  critical: "text-critical-text",
} as const;

function AlertDot({ tone }: { tone: "neutral" | "warning" | "critical" }) {
  if (tone === "neutral") return null;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "absolute -top-0.5 -right-0.5 size-1.5 rounded-full",
        tone === "critical" ? "bg-critical" : "bg-warning",
      )}
    />
  );
}

/**
 * The foot of the rail, players first: lifetime players are what a plan
 * limits, so they lead with a figure and a bar; AI credits follow on one quiet
 * line. When either needs attention its icon gets a dot and one line says what
 * to do, players before credits. Both link to Usage. Nothing here is derived
 * that the server did not send.
 */
function UsageChips({ collapsed = false }: { collapsed?: boolean }) {
  const { orgId, chip, settled } = useAllowanceChip();
  const usage = useEntitlement(orgId ?? "").data;
  const playersRead = usePlayerUsage(orgId ?? "").data;
  if (!orgId || !chip) return null;
  const players = playersRead ? railPlayers(playersRead) : undefined;
  const creditsTone = chip.tone === "neutral" ? "neutral" : chip.tone;
  const creditsLine =
    creditsTone === "neutral" ? undefined : ((usage && railLine(usage)) ?? chip.detail);
  // The most severe line wins; on a tie, players, because they are what a plan limits.
  const rank = { neutral: 0, warning: 1, critical: 2 } as const;
  const playersFirst =
    players?.line !== undefined && (!creditsLine || rank[players.tone] >= rank[creditsTone]);
  const line = playersFirst ? players?.line : creditsLine;
  const lineTone = playersFirst && players ? players.tone : creditsTone;
  const creditsTotal = usage ? usage.credits_used + usage.credits_remaining : 0;
  const creditsShare =
    creditsTotal > 0 && usage ? usage.credits_remaining / creditsTotal : null;

  return (
    <>
      {/* One card, two rows of the same shape — label, figure, bar — players
          first because they are what a plan limits. The same elements in both
          widths: collapsing fades the words and bars (keeping their height,
          which the rail's handle is placed against) and fades a ring in around
          the players icon. */}
      <div className="flex flex-col rounded-[var(--radius-lg)] border border-rule bg-surface p-1">
        {players ? (
          <UsageRow
            orgId={orgId}
            testId="players-chip"
            label={players.label}
            icon={<UserIcon className="size-4" />}
            name="Players"
            value={players.value}
            tone={players.tone}
            share={players.share}
            barLabel="Players onboarded"
            ring
          />
        ) : null}
        <UsageRow
          orgId={orgId}
          testId="credit-chip"
          label={chip.label}
          title={collapsed ? `${chip.label}. ${chip.detail}` : creditsTitle(chip.detail)}
          busy={!settled}
          icon={<SpendIcon className="size-4" />}
          name="AI credits"
          value={creditsValue(chip.label, usage)}
          tone={creditsTone}
          share={settled ? creditsShare : null}
          barLabel="Credits left"
        />
      </div>
      {line ? (
        <div className="rail-fold">
          <div>
            <p
              className={cn(
                "px-2 text-meta",
                TONE_TEXT[lineTone === "neutral" ? "warning" : lineTone],
              )}
            >
              {line}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** "3,760 left" from the figure itself; "None left" at zero; the over and capped states keep their own words. */
function creditsValue(label: string, usage: Entitlement | undefined): string {
  const state = usage?.allowance.state;
  if (state === "spent" || usage?.credits_remaining === 0) return "None left";
  if (usage && (state === "comfortable" || state === "low")) {
    return `${usage.credits_remaining.toLocaleString("en-IN")} left`;
  }
  return label;
}

function UsageRow({
  orgId,
  testId,
  label,
  title,
  busy = false,
  icon,
  name,
  value,
  tone,
  share,
  barLabel,
  ring = false,
}: {
  orgId: string;
  testId: string;
  label: string;
  title?: string;
  busy?: boolean;
  icon: ReactNode;
  name: string;
  value: string;
  tone: "neutral" | "warning" | "critical";
  share: number | null;
  barLabel: string;
  ring?: boolean;
}) {
  const meterTone = tone === "neutral" ? "accent" : tone;
  return (
    <Link
      to="/orgs/$orgId/usage"
      params={{ orgId }}
      title={title ?? label}
      aria-label={label}
      aria-busy={busy}
      data-testid={testId}
      className={cn(
        "flex flex-col gap-1.5 overflow-hidden rounded-[var(--radius-control)] px-1.75 py-1.5 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent in-data-[rail=closed]:px-1",
        busy && "opacity-50",
      )}
    >
      <span className="flex items-center gap-2 text-meta whitespace-nowrap">
        <span
          className={cn(
            "relative grid shrink-0 place-items-center",
            tone === "neutral" ? "text-ink-3" : TONE_TEXT[tone],
          )}
        >
          {share !== null && ring ? (
            <RingMeter
              size="sm"
              value={share}
              tone={meterTone}
              className="absolute top-1/2 left-1/2 -translate-1/2 opacity-0 transition-opacity in-data-[rail=closed]:opacity-100"
            />
          ) : null}
          {icon}
          <AlertDot tone={tone} />
        </span>
        <span className="rail-fade text-ink-2">{name}</span>
        <span
          className={cn(
            "rail-fade ml-auto font-medium tabular-nums",
            tone === "neutral" ? "text-ink" : TONE_TEXT[tone],
          )}
        >
          {value}
        </span>
      </span>
      <span
        role="progressbar"
        aria-label={barLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round((share ?? 0) * 100)}
        className={cn("rail-fade block", share === null && "invisible")}
      >
        <Meter value={share ?? 0} tone={meterTone} />
      </span>
    </Link>
  );
}

/** Studio selection and settings share the same explicit dropdown at every membership count. */
/**
 * Where an organisation-level screen sits, for the strip: the studio, the
 * section, and the page inside it. Without it the strip on Team, Plan or
 * Settings carried nothing but the appearance control.
 */
function OrgCrumb() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const trail = orgTrail(pathname);
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5">
      <StudioCrumb />
      {trail.map((crumb) => (
        <span key={crumb} className="flex min-w-0 items-center gap-1.5">
          <span aria-hidden="true" className="text-ui text-ink-3">
            /
          </span>
          <span className="min-w-0 truncate text-ui font-medium text-ink">{crumb}</span>
        </span>
      ))}
    </nav>
  );
}

/**
 * The studio in the breadcrumb: a link to its home, never a second switcher —
 * the rail's is the one control that changes studio.
 */
function StudioCrumb() {
  const current = useCurrentOrgSummary();
  if (!current) return null;
  return (
    <Link
      to="/orgs/$orgId"
      params={{ orgId: current.id }}
      className="min-w-0 truncate rounded-sm px-2 text-ui font-medium text-ink-2 transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
    >
      {current.name}
    </Link>
  );
}

function StudioName({
  inRail = false,
  collapsed = false,
}: {
  inRail?: boolean;
  collapsed?: boolean;
}) {
  const orgs = useOrgs();
  const current = useCurrentOrgSummary();
  const navigate = useNavigate();

  if (!current) return null;

  return (
    <Dropdown
      className={cn("min-w-0", inRail && "block flex-1 [&>button]:w-full")}
      align={inRail && !collapsed ? "end" : "start"}
      inline={inRail && !collapsed}
      label={`Switch studio: ${current.name}`}
      menuTitle="Studios"
      selectedId={current.id}
      trigger={
        <span
          className={cn(
            "flex h-9 min-w-0 items-center gap-1.5 rounded-sm px-2 text-ui font-medium text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink",
            inRail && "rail-studio w-full gap-2 overflow-hidden bg-sunk/40 text-ink",
          )}
        >
          {inRail ? <Logo className="size-6 shrink-0" /> : null}
          <span className={cn("min-w-0 truncate", inRail && "rail-fade flex-1 text-left")}>
            {current.name}
          </span>
          {inRail ? (
            <span className="rail-fade grid place-items-center">
              <CaretIcon />
            </span>
          ) : (
            <CaretIcon />
          )}
        </span>
      }
      items={[
        ...orgs.map((o) => ({
          id: o.id,
          label: o.name,
          onSelect: () => void navigate({ to: "/orgs/$orgId", params: { orgId: o.id } }),
        })),
        {
          id: "settings",
          label: "Studio settings",
          separatorBefore: true,
          onSelect: () =>
            void navigate({ to: "/orgs/$orgId/settings", params: { orgId: current.id } }),
        },
      ]}
    />
  );
}

function ProjectSwitcher({ projectId }: { projectId: string }) {
  // The organisation this project belongs to, not the first one the viewer
  // happens to be in. Reading `orgs[0]` here meant somebody in two studios saw
  // only the first studio's projects — including while looking at a project
  // from the second, which is not listed and which nothing else links to.
  const orgId = useCurrentOrg();
  const projects = useProjects(orgId);
  const project = useProject(projectId);
  const navigate = useNavigate();

  return (
    <Dropdown
      // `min-w-0`, so the trigger's `truncate` has something to truncate
      // against. A `Dropdown` is an `inline-block`, which as a flex item will
      // not go below its content width without this — which is how the
      // breadcrumb came to paint over the controls beside it.
      className="min-w-0"
      label={`Switch game: ${project.data?.name ?? "Game"}`}
      menuTitle="Games"
      selectedId={projectId}
      trigger={
        <span className="flex h-9 min-w-0 items-center gap-2 rounded-sm border border-rule bg-sunk/45 px-3 text-ui font-medium text-ink transition-colors hover:bg-surface-2/55">
          <GamesIcon className="hidden size-3.5 shrink-0 sm:block" />
          <span className="max-w-28 truncate sm:max-w-40 xl:max-w-56">
            {project.data?.name ?? "Game"}
          </span>
          <CaretIcon />
        </span>
      }
      items={[
        ...(projects.data ?? []).map((p) => ({
          id: p.id,
          label: p.name,
          onSelect: () =>
            void navigate({ to: "/projects/$projectId", params: { projectId: p.id } }),
        })),
        {
          id: "new-project",
          label: "+ New game",
          separatorBefore: true,
          // ?new=1 skips the resume redirect.
          onSelect: () => void navigate({ to: "/start", search: { new: 1 } }),
        },
      ]}
    />
  );
}

/**
 * Which environment the scoped screens are about.
 *
 * Switching stays on the screen you are on where it can — comparing the same
 * screen across two environments is most of what this control is for, and being
 * thrown back to Overview each time makes that a chore.
 *
 * Production takes `critical` **and** the word. Every other environment takes
 * `positive`, which is less a judgement about it than the absence of one.
 */
function EnvironmentSwitcher({
  projectId,
  envId,
}: {
  projectId: string;
  envId: string | undefined;
}) {
  const environments = useEnvironments(projectId);
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const onEnvRoute = Boolean(params.envId);
  const { choose } = useEnvironmentSelection();
  const current = (environments.data ?? []).find((e) => e.id === envId);
  const serving = useServing();

  const go = useCallback(
    (id: string) => {
      choose(id);
      // Only routes that name an environment need to move. Everywhere else —
      // Overview, Game Rules — reads the same selection, so switching updates
      // the page in place, and being thrown onto a different screen for asking
      // "what does this look like in production" would be its own defect.
      if (onEnvRoute) {
        void navigate({ to: ".", params: (p: object) => ({ ...p, envId: id }) });
      }
    },
    [choose, navigate, onEnvRoute],
  );

  if ((environments.data ?? []).length === 0) {
    return <span className="shrink-0 text-meta text-ink-3">no environments</span>;
  }

  return (
    <Dropdown
      // `shrink-0`, so when the breadcrumb runs out of room the project's name
      // is what shortens and this is not. Which environment you are looking at
      // is the one fact the strip uniquely owns — the page heading names the
      // screen and the rail names the project, and neither of them says
      // whether you are about to change production.
      className="shrink-0"
      menuTitle="Environments"
      selectedId={envId}
      trigger={
        <span className="flex h-8 min-w-0 items-center gap-2 rounded-[var(--radius-control)] border border-rule-strong bg-surface-2 px-2.5 transition-colors hover:bg-surface">
          <EnvironmentDot production={current?.kind === "production"} />
          <span className="block max-w-24 truncate text-ui leading-none font-medium text-ink sm:max-w-32">
            {current?.name ?? "Environment"}
          </span>
          {serving ? (
            <>
              <span aria-hidden="true" className="h-3.5 w-px shrink-0 bg-rule-strong" />
              <span
                title={serving.detail}
                className={cn(
                  "max-w-24 truncate text-ui leading-none sm:max-w-32",
                  serving.tone === "warning" ? "text-warning-text" : "text-ink-2",
                )}
              >
                {serving.label}
              </span>
            </>
          ) : null}
          <CaretIcon />
        </span>
      }
      items={(environments.data ?? []).map((e) => ({
        id: e.id,
        label: e.kind === "production" ? `${e.name} — live` : e.name,
        onSelect: () => go(e.id),
      }))}
    />
  );
}

/** The mark on a control that opens a list under itself. */
function CaretIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3 shrink-0 text-ink-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9.5 12 15.5 18 9.5" />
    </svg>
  );
}
